/**
 * Tailwind-styled WeChat Pay top-up modal extracted from llm-web's Subscription page.
 * Renders a self-contained dialog with QR-code payment polling. The component is
 * styling-agnostic at the package level — the consumer must provide tailwind
 * classes (`bg-card`, `text-foreground`, etc.) via their own design tokens.
 *
 * The translation function is injected so this stays decoupled from the host
 * i18n setup. All keys live under the consumer's `subscription.*` namespace.
 */
import { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { QRCodeSVG } from 'qrcode.react'
import { CheckCircle, RefreshCw, Wallet, X } from 'lucide-react'
import type { ApiEnvelope } from '@etherworks/shared-types'

const TOPUP_PRESETS_USD = [5, 10, 20, 50, 100]
const TOPUP_USD_MIN = 0.01
const TOPUP_USD_MAX = 1000

export interface TopUpModalLiteProps {
	onClose: () => void
	onSuccess: () => Promise<void> | void
	/** Translation function from the host's `useTranslation('settings')` (or equivalent). */
	t: (key: string, opts?: any) => string
}

interface TopUpOrder {
	codeUrl: string
	paymentId: string
	amountUSD: number
	amountCNY: number
}

interface QuoteData {
	amountUSD: number
	amountCNY: number
	rate: number
}

interface PaymentStatusData {
	status: string
	provider: string
}

export default function TopUpModalLite({ onClose, onSuccess, t }: TopUpModalLiteProps) {
	const [rate, setRate] = useState<number>(7.0)
	const [amountUSD, setAmountUSD] = useState<number>(10)
	const [customInput, setCustomInput] = useState<string>('')
	const [creating, setCreating] = useState(false)
	const [order, setOrder] = useState<TopUpOrder | null>(null)
	const [paid, setPaid] = useState(false)

	// Fetch live rate on open
	useEffect(() => {
		let cancelled = false
		;(async () => {
			try {
				const { data } = await axios.get<ApiEnvelope<QuoteData>>('/api/billing/topup/quote', { params: { amountUSD: 1 } })
				if (!cancelled && data.code === 0 && typeof data.data?.rate === 'number' && data.data.rate > 0) {
					setRate(data.data.rate)
				}
			} catch {
				// keep default rate
			}
		})()
		return () => { cancelled = true }
	}, [])

	const effectiveUSD = customInput ? Number(customInput) : amountUSD
	const validUSD = Number.isFinite(effectiveUSD) && effectiveUSD >= TOPUP_USD_MIN && effectiveUSD <= TOPUP_USD_MAX
	const belowMinUSD = Number.isFinite(effectiveUSD) && effectiveUSD < TOPUP_USD_MIN
	const amountCNY = validUSD ? Math.round(effectiveUSD * rate * 100) / 100 : 0
	const minAmountLabel = TOPUP_USD_MIN.toFixed(2)

	// Poll payment status while waiting
	useEffect(() => {
		if (!order || paid) return
		const id = setInterval(async () => {
			try {
				const { data } = await axios.get<ApiEnvelope<PaymentStatusData>>(`/api/billing/payment-status/${order.paymentId}`)
				if (data.code !== 0 || !data.data?.status) {
					return
				}
				if (data.data.status === 'completed') {
					setPaid(true)
					clearInterval(id)
					toast.success(t('subscription.topup_success', { usd: order.amountUSD.toFixed(2) }))
					await onSuccess()
				} else if (data.data.status === 'failed' || data.data.status === 'cancelled') {
					clearInterval(id)
					toast.error(t('subscription.topup_failed'))
					setOrder(null)
				}
			} catch {
				// ignore transient errors
			}
		}, 2000)
		return () => clearInterval(id)
	}, [order, paid, onSuccess, t])

	const submit = async () => {
		if (!validUSD) {
			toast.error(t('subscription.topup_invalid_amount'))
			return
		}
		setCreating(true)
		try {
			const { data } = await axios.post<ApiEnvelope<TopUpOrder>>('/api/billing/topup/wechat-pay/create-order', {
				amountUSD: effectiveUSD,
			})
			if (data.code !== 0 || !data.data?.codeUrl || !data.data.paymentId) {
				toast.error(t('subscription.topup_failed'))
				return
			}
			setOrder({
				codeUrl: data.data.codeUrl,
				paymentId: data.data.paymentId,
				amountUSD: data.data.amountUSD,
				amountCNY: data.data.amountCNY,
			})
		} catch (err: any) {
			toast.error(t('subscription.topup_failed'))
		} finally {
			setCreating(false)
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
			<div className="relative w-full max-w-md mx-4 rounded-2xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200">
				<button
					onClick={onClose}
					className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
				>
					<X className="h-4 w-4" />
				</button>

				<div className="flex items-center gap-2 mb-1">
					<Wallet className="h-5 w-5 text-primary" />
					<h2 className="text-lg font-semibold text-foreground">{t('subscription.topup_title')}</h2>
				</div>
				<p className="text-xs text-muted-foreground mb-4">{t('subscription.topup_subtitle')}</p>

				{!order ? (
					<>
						<label className="block text-xs font-medium text-muted-foreground mb-2">
							{t('subscription.topup_amount_usd')}
						</label>
						<div className="grid grid-cols-5 gap-2 mb-3">
							{TOPUP_PRESETS_USD.map(v => (
								<button
									key={v}
									onClick={() => { setAmountUSD(v); setCustomInput('') }}
									className={`py-2 rounded-lg text-sm font-semibold transition-colors border ${
										!customInput && amountUSD === v
											? 'bg-primary text-primary-foreground border-primary'
											: 'bg-background text-foreground border-border hover:border-primary/50'
									}`}
								>
									${v}
								</button>
							))}
						</div>
						<input
							type="number"
							min={TOPUP_USD_MIN}
							max={TOPUP_USD_MAX}
							step="0.01"
							value={customInput}
							onChange={e => setCustomInput(e.target.value)}
							placeholder={t('subscription.topup_custom_placeholder')}
							className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 mb-2"
						/>
						<p className="text-[11px] text-muted-foreground mb-4">
							{t('subscription.topup_rate_hint', { rate: rate.toFixed(4) })}
						</p>
						<p className={`text-[11px] mb-4 ${belowMinUSD ? 'text-red-600' : 'text-muted-foreground'}`}>
							{t('subscription.topup_min_amount_hint', { min: minAmountLabel })}
						</p>

						<button
							onClick={submit}
							disabled={!validUSD || creating}
							className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{creating ? (
								<><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {t('subscription.topup_creating')}</>
							) : (
								validUSD
									? t('subscription.topup_pay_cny', { cny: amountCNY.toFixed(2), usd: effectiveUSD.toFixed(2) })
									: belowMinUSD
										? t('subscription.topup_min_amount_button', { min: minAmountLabel })
										: t('subscription.topup_invalid_amount')
							)}
						</button>
					</>
				) : paid ? (
					<div className="flex flex-col items-center py-6">
						<CheckCircle className="h-12 w-12 text-emerald-500 mb-3" />
						<p className="text-base font-semibold text-foreground mb-1">
							{t('subscription.topup_success', { usd: order.amountUSD.toFixed(2) })}
						</p>
						<button
							onClick={onClose}
							className="mt-4 rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
						>
							{t('subscription.topup_close')}
						</button>
					</div>
				) : (
					<div className="flex flex-col items-center">
						<p className="text-sm font-semibold text-foreground mb-1">
							¥{order.amountCNY.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">≈ ${order.amountUSD.toFixed(2)} USD</span>
						</p>
						<p className="text-xs text-muted-foreground mb-3">{t('subscription.topup_scan')}</p>
						<div className="bg-white p-3 rounded-xl border border-border">
							<QRCodeSVG value={order.codeUrl} size={200} />
						</div>
						<div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
							<RefreshCw className="h-3.5 w-3.5 animate-spin" />
							<span>{t('subscription.topup_waiting')}</span>
						</div>
						<button
							onClick={() => setOrder(null)}
							className="mt-4 text-xs text-muted-foreground hover:text-foreground underline"
						>
							{t('subscription.topup_back')}
						</button>
					</div>
				)}
			</div>
		</div>
	)
}
